import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  RefreshCw, Check, X, Edit2, Users, Shield,
  BarChart3, Download, Loader2, ChevronUp, ChevronDown, Trash2, Database,
  MessageSquare, ThumbsUp, ThumbsDown, AlertCircle, CheckCircle, XCircle,
  FileText, CalendarClock,
} from 'lucide-react'
import { PromptsTab } from '@/components/admin/PromptsTab'
import { SchedulesTab } from '@/components/admin/SchedulesTab'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { adminApi } from '@/services/api/admin'
import type { PendingRequest, User, UserStatistics, KnowledgeBaseStatistics } from '@/services/api/admin'
import type { PendingFeedback } from '@/types/api'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/apiError'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

/** Max length of the reviewer's note; mirrors AdminFeedbackReviewRequest.comment. */
const ADMIN_COMMENT_MAX = 5000

export function AdminPage() {
  const { user, userProfile, isLoading } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('requests')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Fetch pending counts for tab badges
  const { data: pendingRequests } = useQuery({
    queryKey: ['pendingRequests'],
    queryFn: () => adminApi.getPendingRequests(),
  })

  const { data: feedbackStats } = useQuery({
    queryKey: ['feedbackStats'],
    queryFn: () => adminApi.getFeedbackStats(),
  })

  const pendingRequestsCount = pendingRequests?.length || 0
  const pendingFeedbackCount = feedbackStats?.pending || 0

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => { setError(null); setSuccess(null) }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, success])

  // Show loading while fetching user profile
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  // Check role from user object (populated from backend) or userProfile
  const userRole = user?.role || userProfile?.role
  if (!user || !userRole || !['Admin', 'SuperUser'].includes(userRole)) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-6"><p className="text-red-600">Access denied. Admin privileges required.</p></Card>
      </div>
    )
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <motion.div variants={itemVariants} className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage users, access requests, and view analytics</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries()}>
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </motion.div>

      {error && (
        <motion.div variants={itemVariants}>
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
          </div>
        </motion.div>
      )}
      {success && (
        <motion.div variants={itemVariants}>
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}><X className="h-4 w-4" /></button>
          </div>
        </motion.div>
      )}

      <motion.div variants={itemVariants}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="requests" className="flex items-center gap-2 relative">
              <Shield className="h-4 w-4" />
              Access Requests
              {pendingRequestsCount > 0 && (
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                  {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />Users
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />Analytics
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-2 relative">
              <MessageSquare className="h-4 w-4" />
              Issue Feedback
              {pendingFeedbackCount > 0 && (
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                  {pendingFeedbackCount > 99 ? '99+' : pendingFeedbackCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="schedules" className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />Schedules
            </TabsTrigger>
            <TabsTrigger value="prompts" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              AI Prompts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            <AccessRequestsTab setError={setError} setSuccess={setSuccess} />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab currentUserRole={userRole} setError={setError} setSuccess={setSuccess} />
          </TabsContent>
          <TabsContent value="analytics">
            <AnalyticsTab setError={setError} />
          </TabsContent>
          <TabsContent value="feedback">
            <FeedbackReviewTab setError={setError} setSuccess={setSuccess} />
          </TabsContent>
          <TabsContent value="prompts">
            <PromptsTab />
          </TabsContent>
          <TabsContent value="schedules">
            <SchedulesTab setError={setError} setSuccess={setSuccess} />
          </TabsContent>

        </Tabs>
      </motion.div>
    </motion.div>
  )
}

function AccessRequestsTab({ setError, setSuccess }: { setError: (m: string | null) => void; setSuccess: (m: string | null) => void }) {
  const queryClient = useQueryClient()
  const [selectedRequest, setSelectedRequest] = useState<PendingRequest | null>(null)
  const [selectedKBs, setSelectedKBs] = useState<string[]>([])

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['pendingRequests'],
    queryFn: () => adminApi.getPendingRequests(),
  })

  const approveMutation = useMutation({
    mutationFn: ({ requestId, kbs }: { requestId: number; kbs: string[] }) => adminApi.approveRequest(requestId, kbs),
    onSuccess: () => {
      setSuccess('Request approved!')
      queryClient.invalidateQueries({ queryKey: ['pendingRequests'] })
      setSelectedRequest(null)
      setSelectedKBs([])
    },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const rejectMutation = useMutation({
    mutationFn: (id: number) => adminApi.rejectRequest(id),
    onSuccess: () => {
      setSuccess('Request rejected')
      queryClient.invalidateQueries({ queryKey: ['pendingRequests'] })
    },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>

  return (
    <Card>
      <CardHeader><CardTitle>Pending Access Requests ({requests.length})</CardTitle></CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No pending access requests</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">User</th>
                  <th className="text-left p-3 font-medium">Requested KBs</th>
                  <th className="text-left p-3 font-medium">Reason</th>
                  <th className="text-left p-3 font-medium">Requested</th>
                  <th className="text-center p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      <div className="font-medium">{r.user_name}</div>
                      <div className="text-xs text-muted-foreground">{r.user_email}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {r.requested_kbs.slice(0, 3).map((kb, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{kb}</Badge>
                        ))}
                        {r.requested_kbs.length > 3 && <Badge variant="secondary" className="text-xs">+{r.requested_kbs.length - 3}</Badge>}
                      </div>
                    </td>
                    <td className="p-3 max-w-[200px] truncate">{r.reason || 'No reason'}</td>
                    <td className="p-3">{new Date(r.requested_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <div className="flex justify-center gap-2">
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => { setSelectedRequest(r); setSelectedKBs(r.requested_kbs) }}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => { if (confirm('Reject?')) rejectMutation.mutate(r.id) }}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Approve Access Request</DialogTitle>
              <DialogDescription>Select KBs to approve for {selectedRequest?.user_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4 overflow-y-auto flex-1 min-h-0">
              {selectedRequest?.requested_kbs.map((kb) => (
                <label key={kb} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedKBs.includes(kb)}
                    onCheckedChange={(c) => c ? setSelectedKBs([...selectedKBs, kb]) : setSelectedKBs(selectedKBs.filter(k => k !== kb))}
                  />
                  <span>{kb}</span>
                </label>
              ))}
            </div>
            <DialogFooter className="flex-shrink-0">
              <Button variant="outline" onClick={() => setSelectedRequest(null)}>Cancel</Button>
              <Button
                onClick={() => selectedRequest && approveMutation.mutate({ requestId: selectedRequest.id, kbs: selectedKBs })}
                disabled={!selectedKBs.length || approveMutation.isPending}
              >
                {approveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

function UsersTab({ currentUserRole, setError, setSuccess }: { currentUserRole: string; setError: (m: string | null) => void; setSuccess: (m: string | null) => void }) {
  const queryClient = useQueryClient()
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editingRole, setEditingRole] = useState('')
  const [editingPerms, setEditingPerms] = useState<string[]>([])
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null)
  const [kbSearchQuery, setKbSearchQuery] = useState('')

  const { data: usersData, isLoading } = useQuery({ queryKey: ['allUsers'], queryFn: () => adminApi.getAllUsers() })
  const { data: availableKBs = [] } = useQuery({ queryKey: ['availableKBs'], queryFn: () => adminApi.getAvailableKnowledgeBases() })

  const updateRoleMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) => adminApi.updateUserRole(email, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['allUsers'] }),
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const updatePermsMutation = useMutation({
    mutationFn: ({ email, kbs }: { email: string; kbs: string[] }) => adminApi.updateUserPermissions(email, kbs),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['allUsers'] }),
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const deleteMutation = useMutation({
    mutationFn: (email: string) => adminApi.deleteUser(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allUsers'] })
      setSuccess('User deleted successfully')
      setDeleteConfirmUser(null)
    },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const users = usersData?.users || []

  const handleSaveUser = async () => {
    if (!editingUser) return
    try {
      if (editingRole !== editingUser.role && currentUserRole === 'SuperUser') {
        await updateRoleMutation.mutateAsync({ email: editingUser.email, role: editingRole })
      }
      if (editingPerms.sort().join() !== editingUser.permissions.sort().join()) {
        await updatePermsMutation.mutateAsync({ email: editingUser.email, kbs: editingPerms })
      }
      setSuccess('User updated!')
      setEditingUser(null)
      setKbSearchQuery('')
    } catch {}
  }

  const toggleKBPermission = (kb: string) => {
    if (editingPerms.includes(kb)) {
      setEditingPerms(editingPerms.filter(k => k !== kb))
    } else {
      setEditingPerms([...editingPerms, kb])
    }
  }

  const filteredKBs = availableKBs.filter(kb =>
    kb.toLowerCase().includes(kbSearchQuery.toLowerCase())
  )

  const getRoleColor = (r: string) => r === 'SuperUser' ? 'bg-red-100 text-red-800' : r === 'Admin' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>

  return (
    <Card>
      <CardHeader><CardTitle>Users ({users.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Knowledge Bases</th>
                <th className="text-left p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.email} className="border-b hover:bg-muted/50">
                  <td className="p-3">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </td>
                  <td className="p-3">
                    <Badge className={getRoleColor(u.role)}>{u.role}</Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[250px]">
                      {u.permissions.slice(0, 2).map((kb, i) => <Badge key={i} variant="outline" className="text-xs">{kb}</Badge>)}
                      {u.permissions.length > 2 && <Badge variant="secondary" className="text-xs">+{u.permissions.length - 2}</Badge>}
                      {u.permissions.length === 0 && <span className="text-muted-foreground text-xs">None</span>}
                    </div>
                  </td>
                  <td className="p-3"><Badge variant={u.is_active ? 'default' : 'secondary'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="p-3">
                    <div className="flex justify-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingUser(u); setEditingRole(u.role); setEditingPerms(u.permissions); setKbSearchQuery('') }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteConfirmUser(u)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={!!editingUser} onOpenChange={() => { setEditingUser(null); setKbSearchQuery('') }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Modify role and knowledge base permissions for {editingUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 flex-1 overflow-hidden flex flex-col">
              {/* User Info */}
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="font-medium">{editingUser?.name}</div>
                <div className="text-sm text-muted-foreground">{editingUser?.email}</div>
              </div>

              {/* Role Selection (SuperUser only) */}
              {currentUserRole === 'SuperUser' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Role</label>
                  <Select value={editingRole} onValueChange={setEditingRole}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="User">User</SelectItem>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="SuperUser">SuperUser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Knowledge Base Permissions */}
              <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Knowledge Base Permissions
                  </label>
                  <Badge variant="secondary">{editingPerms.length} selected</Badge>
                </div>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Search knowledge bases..."
                  value={kbSearchQuery}
                  onChange={(e) => setKbSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />

                {/* Select All / Deselect All */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingPerms([...availableKBs])}
                    className="text-xs"
                  >
                    Select All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingPerms([])}
                    className="text-xs"
                  >
                    Deselect All
                  </Button>
                </div>

                {/* KB List */}
                <div className="flex-1 overflow-y-auto border rounded-md p-2 space-y-1 min-h-[200px] max-h-[300px]">
                  {filteredKBs.map((kb) => (
                    <label key={kb} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded">
                      <Checkbox
                        checked={editingPerms.includes(kb)}
                        onCheckedChange={() => toggleKBPermission(kb)}
                      />
                      <span className="text-sm">{kb}</span>
                    </label>
                  ))}
                  {filteredKBs.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No knowledge bases found</p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditingUser(null); setKbSearchQuery('') }}>Cancel</Button>
              <Button
                onClick={handleSaveUser}
                disabled={updateRoleMutation.isPending || updatePermsMutation.isPending}
              >
                {(updateRoleMutation.isPending || updatePermsMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirmUser} onOpenChange={() => setDeleteConfirmUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this user? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="font-medium">{deleteConfirmUser?.name}</div>
                <div className="text-sm text-muted-foreground">{deleteConfirmUser?.email}</div>
                <div className="mt-2 text-sm">
                  <span className="text-red-600">This will remove:</span>
                  <ul className="list-disc list-inside text-muted-foreground mt-1">
                    <li>User profile</li>
                    <li>All KB permissions ({deleteConfirmUser?.permissions.length || 0} KBs)</li>
                  </ul>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmUser(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => deleteConfirmUser && deleteMutation.mutate(deleteConfirmUser.email)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

function AnalyticsTab({ setError }: { setError: (m: string | null) => void }) {
  const [exportLoading, setExportLoading] = useState<string | null>(null)
  const [userSort, setUserSort] = useState<{ key: keyof UserStatistics; dir: 'asc' | 'desc' }>({ key: 'total_jobs', dir: 'desc' })
  const [kbSort, setKbSort] = useState<{ key: keyof KnowledgeBaseStatistics; dir: 'asc' | 'desc' }>({ key: 'total_jobs', dir: 'desc' })

  const { data: summary } = useQuery({ queryKey: ['analyticsSummary'], queryFn: () => adminApi.getAnalyticsSummary() })
  const { data: userStatsData } = useQuery({ queryKey: ['userStatistics'], queryFn: () => adminApi.getUserStatistics() })
  const { data: kbStatsData } = useQuery({ queryKey: ['kbStatistics'], queryFn: () => adminApi.getKnowledgeBaseStatistics() })

  const sortedUsers = useMemo(() => [...(userStatsData?.users || [])].sort((a, b) => {
    const av = a[userSort.key] ?? 0, bv = b[userSort.key] ?? 0
    return userSort.dir === 'asc' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1)
  }), [userStatsData, userSort])

  const sortedKbs = useMemo(() => [...(kbStatsData?.knowledge_bases || [])].sort((a, b) => {
    const av = a[kbSort.key] ?? 0, bv = b[kbSort.key] ?? 0
    return kbSort.dir === 'asc' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1)
  }), [kbStatsData, kbSort])

  const handleExport = async (type: 'users' | 'knowledge_bases', format: 'csv' | 'excel') => {
    const key = type + '_' + format
    setExportLoading(key)
    try {
      const blob = await adminApi.exportAnalytics(type, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = type + '.' + (format === 'excel' ? 'xlsx' : 'csv')
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: unknown) { setError(getApiErrorMessage(e)) }
    finally { setExportLoading(null) }
  }

  const SortTh = ({ label, sortKey, sort, setSort }: { label: string; sortKey: string; sort: any; setSort: any }) => (
    <th
      className="text-left p-3 cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => setSort({ key: sortKey, dir: sort.key === sortKey && sort.dir === 'desc' ? 'asc' : 'desc' })}
    >
      <div className="flex items-center gap-1">
        {label}
        {sort.key === sortKey && (sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </div>
    </th>
  )

  return (
    <div className="space-y-6">
      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.total_articles}</div><div className="text-sm text-muted-foreground">Total Articles</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.evaluated_articles}</div><div className="text-sm text-muted-foreground">Evaluated Articles</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.enhanced_articles}</div><div className="text-sm text-muted-foreground">Enhanced Articles</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.enhancement_rate}%</div><div className="text-sm text-muted-foreground">Enhancement Rate</div></CardContent></Card>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.total_users}</div><div className="text-sm text-muted-foreground">Active Users</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.total_knowledge_bases}</div><div className="text-sm text-muted-foreground">Knowledge Bases</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.evaluation_jobs} <span className="text-sm font-normal text-muted-foreground">({summary.completed_evaluation_jobs} done)</span></div><div className="text-sm text-muted-foreground">Evaluation Jobs</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{summary.enhancement_jobs} <span className="text-sm font-normal text-muted-foreground">({summary.completed_enhancement_jobs} done)</span></div><div className="text-sm text-muted-foreground">Enhancement Jobs</div></CardContent></Card>
          </div>
        </>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>User Statistics</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleExport('users', 'excel')} disabled={!!exportLoading}>
              {exportLoading === 'users_excel' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleExport('users', 'csv')} disabled={!!exportLoading}>
              {exportLoading === 'users_csv' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <SortTh label="User" sortKey="user_name" sort={userSort} setSort={setUserSort} />
                  <SortTh label="Jobs" sortKey="total_jobs" sort={userSort} setSort={setUserSort} />
                  <SortTh label="Completed" sortKey="completed_jobs" sort={userSort} setSort={setUserSort} />
                  <SortTh label="Articles" sortKey="articles_processed" sort={userSort} setSort={setUserSort} />
                  <SortTh label="Success Rate" sortKey="job_success_rate" sort={userSort} setSort={setUserSort} />
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((s) => (
                  <tr key={s.user_email} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      <div className="font-medium">{s.user_name}</div>
                      <div className="text-xs text-muted-foreground">{s.user_email}</div>
                    </td>
                    <td className="p-3">{s.total_jobs}</td>
                    <td className="p-3">{s.completed_jobs}</td>
                    <td className="p-3">{s.articles_processed}</td>
                    <td className="p-3">
                      <Badge className={cn(
                        s.job_success_rate >= 80 ? 'bg-green-100 text-green-800' :
                        s.job_success_rate >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      )}>{s.job_success_rate}%</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Knowledge Base Statistics</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleExport('knowledge_bases', 'excel')} disabled={!!exportLoading}>
              {exportLoading === 'knowledge_bases_excel' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleExport('knowledge_bases', 'csv')} disabled={!!exportLoading}>
              {exportLoading === 'knowledge_bases_csv' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <SortTh label="Knowledge Base" sortKey="knowledge_base" sort={kbSort} setSort={setKbSort} />
                  <SortTh label="Jobs" sortKey="total_jobs" sort={kbSort} setSort={setKbSort} />
                  <SortTh label="Articles" sortKey="total_articles_processed" sort={kbSort} setSort={setKbSort} />
                  <SortTh label="Enhanced" sortKey="articles_pushed_to_servicenow" sort={kbSort} setSort={setKbSort} />
                  <SortTh label="Rate" sortKey="push_success_rate" sort={kbSort} setSort={setKbSort} />
                </tr>
              </thead>
              <tbody>
                {sortedKbs.map((s) => (
                  <tr key={s.knowledge_base} className="border-b hover:bg-muted/50">
                    <td className="p-3 font-medium">{s.knowledge_base}</td>
                    <td className="p-3">{s.total_jobs}</td>
                    <td className="p-3">{s.total_articles_processed}</td>
                    <td className="p-3">{s.articles_pushed_to_servicenow}</td>
                    <td className="p-3">
                      <Badge className={cn(
                        s.push_success_rate >= 30 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      )}>{s.push_success_rate}%</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function FeedbackReviewTab({ setError, setSuccess }: { setError: (m: string | null) => void; setSuccess: (m: string | null) => void }) {
  const queryClient = useQueryClient()
  const [selectedFeedback, setSelectedFeedback] = useState<PendingFeedback | null>(null)
  const [adminComment, setAdminComment] = useState('')
  const [activeFilter, setActiveFilter] = useState<'pending' | 'approved' | 'rejected'>('pending')
  const [selectedKB, setSelectedKB] = useState<string>('all')

  // Get feedback stats
  const { data: stats } = useQuery({
    queryKey: ['feedbackStats'],
    queryFn: () => adminApi.getFeedbackStats(),
  })

  // Get pending feedback
  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['pendingFeedback'],
    queryFn: () => adminApi.getPendingFeedback({ limit: 100 }),
    enabled: activeFilter === 'pending',
  })

  // Get approved feedback
  const { data: approvedData, isLoading: approvedLoading } = useQuery({
    queryKey: ['approvedFeedback'],
    queryFn: () => adminApi.getReviewedFeedback({ status: 'approved', limit: 100 }),
    enabled: activeFilter === 'approved',
  })

  // Get rejected feedback
  const { data: rejectedData, isLoading: rejectedLoading } = useQuery({
    queryKey: ['rejectedFeedback'],
    queryFn: () => adminApi.getReviewedFeedback({ status: 'rejected', limit: 100 }),
    enabled: activeFilter === 'rejected',
  })

  const isLoading = activeFilter === 'pending' ? pendingLoading : activeFilter === 'approved' ? approvedLoading : rejectedLoading
  const baseFeedbackList = activeFilter === 'pending'
    ? (pendingData?.feedback || [])
    : activeFilter === 'approved'
      ? (approvedData?.feedback || [])
      : (rejectedData?.feedback || [])

  // Knowledge-base options derived from the feedback currently loaded for this
  // status, so the dropdown only offers KBs that actually have feedback to show.
  const knowledgeBases = Array.from(
    new Set(baseFeedbackList.map((fb) => fb.knowledge_base).filter((kb): kb is string => !!kb))
  ).sort()

  // Fall back to "all" when the chosen KB has no rows in the current status
  // (e.g. after switching status tabs), so the table never looks empty.
  const effectiveKB = selectedKB !== 'all' && knowledgeBases.includes(selectedKB) ? selectedKB : 'all'

  const feedbackList = effectiveKB === 'all'
    ? baseFeedbackList
    : baseFeedbackList.filter((fb) => fb.knowledge_base === effectiveKB)

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: ({ feedbackId, decision, comment }: { feedbackId: number; decision: 'approved' | 'rejected'; comment?: string }) =>
      adminApi.reviewFeedback({ feedback_id: feedbackId, decision, comment }),
    onSuccess: (_, variables) => {
      setSuccess(`Feedback ${variables.decision}!`)
      queryClient.invalidateQueries({ queryKey: ['pendingFeedback'] })
      queryClient.invalidateQueries({ queryKey: ['approvedFeedback'] })
      queryClient.invalidateQueries({ queryKey: ['rejectedFeedback'] })
      queryClient.invalidateQueries({ queryKey: ['feedbackStats'] })
      setSelectedFeedback(null)
      setAdminComment('')
    },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const handleReview = (decision: 'approved' | 'rejected') => {
    if (!selectedFeedback) return
    reviewMutation.mutate({
      feedbackId: selectedFeedback.id,
      decision,
      comment: adminComment || undefined,
    })
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getFeedbackTypeLabel = (type: string) => {
    switch (type) {
      case 'not_an_error': return 'Not an Error'
      case 'detection_error': return 'Detection Error'
      case 'other': return 'Other Reason'
      // Legacy values (backward compatibility)
      case 'false_positive': return 'Invalid Issue'
      case 'valid': return 'Valid Issue'
      case 'severity_wrong': return 'Wrong Severity'
      default: return type.replace(/_/g, ' ')
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards - Clickable to filter */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card
            className={cn('cursor-pointer transition-all hover:shadow-md', activeFilter === 'pending' && 'ring-2 ring-yellow-500')}
            onClick={() => setActiveFilter('pending')}
          >
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-sm text-muted-foreground">Pending Review</div>
            </CardContent>
          </Card>
          <Card
            className={cn('cursor-pointer transition-all hover:shadow-md', activeFilter === 'approved' && 'ring-2 ring-green-500')}
            onClick={() => setActiveFilter('approved')}
          >
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
              <div className="text-sm text-muted-foreground">Approved</div>
            </CardContent>
          </Card>
          <Card
            className={cn('cursor-pointer transition-all hover:shadow-md', activeFilter === 'rejected' && 'ring-2 ring-red-500')}
            onClick={() => setActiveFilter('rejected')}
          >
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
              <div className="text-sm text-muted-foreground">Rejected</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total Feedback</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feedback Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {activeFilter === 'pending' && `Pending Issue Feedback (${feedbackList.length})`}
              {activeFilter === 'approved' && `Approved Feedback (${feedbackList.length})`}
              {activeFilter === 'rejected' && `Rejected Feedback (${feedbackList.length})`}
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Knowledge Base:</span>
              <Select value={effectiveKB} onValueChange={setSelectedKB}>
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="All Knowledge Bases" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Knowledge Bases</SelectItem>
                  {knowledgeBases.map((kb) => (
                    <SelectItem key={kb} value={kb}>{kb}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : feedbackList.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {activeFilter === 'pending' ? (
                <>
                  <ThumbsUp className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No pending feedback to review</p>
                </>
              ) : activeFilter === 'approved' ? (
                <>
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No approved feedback yet</p>
                </>
              ) : (
                <>
                  <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                  <p>No rejected feedback yet</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Article</th>
                    <th className="text-left p-3 font-medium">Knowledge Base</th>
                    <th className="text-left p-3 font-medium">Issue</th>
                    <th className="text-left p-3 font-medium">Feedback</th>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-center p-3 font-medium">{activeFilter === 'pending' ? 'Actions' : 'Status'}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbackList.map((fb) => (
                    <tr key={fb.id} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <div className="font-medium text-xs">{fb.display_number}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[150px]">{fb.article_title}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs truncate max-w-[160px]" title={fb.knowledge_base || undefined}>
                          {fb.knowledge_base || <span className="text-muted-foreground">—</span>}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getSeverityColor(fb.issue_severity)}>
                            {fb.issue_severity.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{fb.issue_category}</span>
                        </div>
                        <div className="text-xs truncate max-w-[200px]">{fb.issue_title}</div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="mb-1">
                          {getFeedbackTypeLabel(fb.feedback_type)}
                        </Badge>
                        {fb.feedback_reason && (
                          <div className="text-xs text-muted-foreground">{fb.feedback_reason.replace(/_/g, ' ')}</div>
                        )}
                        {activeFilter !== 'pending' && fb.admin_comment && (
                          <div
                            className="text-xs mt-1 italic text-muted-foreground border-l-2 border-muted pl-2 line-clamp-3 max-w-[240px]"
                            title={fb.admin_comment}
                          >
                            <span className="not-italic font-medium">Reviewer note: </span>
                            {fb.admin_comment}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="text-xs">{fb.user_email}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs">{new Date(fb.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center items-center gap-2">
                          {activeFilter === 'pending' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedFeedback(fb); setAdminComment(fb.admin_comment || '') }}
                            >
                              Review
                            </Button>
                          ) : activeFilter === 'approved' ? (
                            <>
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approved
                              </Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-muted-foreground hover:text-red-600"
                                onClick={() => { setSelectedFeedback(fb); setAdminComment(fb.admin_comment || '') }}
                              >
                                Change
                              </Button>
                            </>
                          ) : (
                            <>
                              <Badge className="bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Rejected
                              </Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-muted-foreground hover:text-green-600"
                                onClick={() => { setSelectedFeedback(fb); setAdminComment(fb.admin_comment || '') }}
                              >
                                Change
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-base sm:text-lg">
              {activeFilter === 'pending' ? 'Review Feedback' : 'Change Decision'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {activeFilter === 'pending'
                ? 'Approve or reject this user feedback on an issue.'
                : `This feedback was previously ${activeFilter}. You can change the decision.`
              }
            </DialogDescription>
          </DialogHeader>

          {selectedFeedback && (
            <div className="space-y-3 sm:space-y-4 py-2 sm:py-4 overflow-y-auto flex-1 min-h-0">
              {/* Article Info */}
              <div className="bg-muted/50 rounded-lg p-2 sm:p-3">
                <div className="text-xs text-muted-foreground">Article</div>
                <div className="font-medium text-sm sm:text-base truncate">{selectedFeedback.display_number}</div>
                <div className="text-xs sm:text-sm text-muted-foreground truncate">{selectedFeedback.article_title}</div>
              </div>

              {/* Issue Info */}
              <div className="border rounded-lg p-2 sm:p-3">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className={cn(getSeverityColor(selectedFeedback.issue_severity), 'text-xs')}>
                    {selectedFeedback.issue_severity.toUpperCase()}
                  </Badge>
                  <span className="text-xs sm:text-sm text-muted-foreground">{selectedFeedback.issue_category}</span>
                </div>
                <div className="font-medium text-sm sm:text-base">{selectedFeedback.issue_title}</div>
                {selectedFeedback.issue_description && selectedFeedback.issue_description !== selectedFeedback.issue_title && (
                  <div className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-3">{selectedFeedback.issue_description}</div>
                )}
                {selectedFeedback.issue_evidence && (
                  <div className="text-xs sm:text-sm text-gray-500 mt-2 italic line-clamp-2">
                    <span className="font-medium not-italic">Evidence:</span> "{selectedFeedback.issue_evidence}"
                  </div>
                )}
              </div>

              {/* User Feedback */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-3">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                  <span className="font-medium text-yellow-800 text-sm">User Feedback</span>
                </div>
                <div className="text-xs sm:text-sm">
                  <span className="font-medium">Type:</span> {getFeedbackTypeLabel(selectedFeedback.feedback_type)}
                </div>
                {selectedFeedback.feedback_reason && (
                  <div className="text-xs sm:text-sm">
                    <span className="font-medium">Reason:</span> {selectedFeedback.feedback_reason.replace(/_/g, ' ')}
                  </div>
                )}
                {selectedFeedback.feedback_comment && (
                  <div className="text-xs sm:text-sm mt-1 sm:mt-2 italic">"{selectedFeedback.feedback_comment}"</div>
                )}
                <div className="text-xs text-muted-foreground mt-1 sm:mt-2 truncate">
                  Submitted by {selectedFeedback.user_email}
                </div>
              </div>

              {/* Previous review context (when changing an existing decision) */}
              {selectedFeedback.admin_reviewed_at && (
                <div className="text-xs text-muted-foreground">
                  Previously reviewed by {selectedFeedback.admin_email || 'admin'} on{' '}
                  {new Date(selectedFeedback.admin_reviewed_at).toLocaleDateString()}
                </div>
              )}

              {/* Admin Comment */}
              <div>
                <div className="flex items-baseline justify-between mb-1 sm:mb-2">
                  <label className="text-xs sm:text-sm font-medium block">Admin Comment (optional)</label>
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      adminComment.length > ADMIN_COMMENT_MAX * 0.9
                        ? 'text-amber-600'
                        : 'text-muted-foreground',
                    )}
                  >
                    {adminComment.length.toLocaleString()} / {ADMIN_COMMENT_MAX.toLocaleString()}
                  </span>
                </div>
                <Textarea
                  placeholder="Add a note..."
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  maxLength={ADMIN_COMMENT_MAX}
                  rows={4}
                  className="text-sm"
                />
              </div>

              {/* Impact Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 text-xs sm:text-sm">
                <div className="font-medium text-blue-800 mb-1">If approved:</div>
                <p className="text-blue-700">
                  This issue will be skipped in future evaluations and enhancements.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2 pt-2 border-t mt-2">
            <Button variant="outline" onClick={() => setSelectedFeedback(null)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReview('rejected')}
              disabled={reviewMutation.isPending}
              className="w-full sm:w-auto"
            >
              {reviewMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <ThumbsDown className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => handleReview('approved')}
              disabled={reviewMutation.isPending}
              className="w-full sm:w-auto"
            >
              {reviewMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <ThumbsUp className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AdminPage
